import React from 'react';
import PropTypes from 'prop-types';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { canAccessRoute, ROUTE_PERMISSIONS } from '../utils/routePermissions';
import { isCustomerPortalOnlyUser } from '../utils/customerAccountApproval';
import { Box, Typography, CircularProgress, Button } from '@mui/joy';
import MainLayout from './MainLayout';
import CustomerLayout from '../../home/views/CustomerLayout';

// Loading component
const LoadingSpinner = () => {
  const { t } = useTranslation('ui');
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        gap: 2,
      }}
    >
      <CircularProgress size="lg" />
      <Typography level="body1" color="neutral">
        {t('protected.loading')}
      </Typography>
    </Box>
  );
};

// Permission denied component
const PermissionDenied = () => {
  const { t } = useTranslation('ui');
  const navigate = useNavigate();
  const location = useLocation();

  const handleBackToHome = () => {
    const p = location.pathname;
    const isCustomerContext =
      p.startsWith('/customer-') && p !== '/customer-login';
    navigate(isCustomerContext ? '/customer-home' : '/home');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        gap: 2,
        textAlign: 'center',
        padding: 4,
      }}
    >
      <Typography level="h1" color="danger">
        {t('protected.accessDenied')}
      </Typography>
      <Typography level="body1" color="neutral">
        {t('protected.noPermission')}
      </Typography>
      <Typography level="body2" color="neutral">
        {t('protected.contactAdmin')}
      </Typography>
      <Button
        variant="solid"
        color="primary"
        onClick={handleBackToHome}
        sx={{ mt: 2 }}
      >
        {t('protected.backToHome')}
      </Button>
    </Box>
  );
};

// Protected Route component
const ProtectedRoute = ({ 
  children, 
  requiredPermission, // Single permission to check
  requiredPermissions, // Array of permissions to check
  requireAll = false, // If true, user must have ALL permissions; if false, user needs ANY permission
  requiredRoles = [], // Array of roles to check (for backward compatibility)
  fallback = <PermissionDenied />,
  redirectTo = '/login'
}) => {
  const { isAuthenticated, loading, userPermissions, userRoles, user } = useAuth();
  const { hasPermission, hasAnyPermission, hasAllPermissions, hasAnyRole, isSuperAdmin } = usePermissions();
  const location = useLocation();

  // Show loading while checking authentication
  if (loading) {
    return <LoadingSpinner />;
  }

  // Get current route path for role-based access check (needed for redirect choice)
  const currentPath = location.pathname;

  // Customer portal routes: /customer-* except the login screen
  const isCustomerRoute =
    currentPath.startsWith('/customer-') && currentPath !== '/customer-login';

  // Redirect to login if not authenticated (customer routes → customer-login, others → login)
  if (!isAuthenticated) {
    const loginPath = isCustomerRoute ? '/customer-login' : redirectTo;
    return <Navigate to={loginPath} replace />;
  }
  
  // Determine which layout to use based on route
  // Customer routes always use CustomerLayout, all other routes use MainLayout
  const LayoutComponent = isCustomerRoute ? CustomerLayout : MainLayout;

  // Customer portal: require sign-in only; no role/permission checks
  if (isCustomerRoute) {
    return <LayoutComponent>{children}</LayoutComponent>;
  }

  // Customer-only accounts (no staff permissions) belong in the customer portal.
  if (isCustomerPortalOnlyUser(userRoles, userPermissions, user)) {
    return <Navigate to="/customer-home" replace />;
  }

  // Super admin bypass
  if (isSuperAdmin()) {
    return <LayoutComponent>{children}</LayoutComponent>;
  }

  // Check if this is a public route first
  const routeConfig = ROUTE_PERMISSIONS[currentPath];
  if (routeConfig && routeConfig.isPublic) {
    // Public routes are accessible to everyone, use appropriate layout
    return <LayoutComponent>{children}</LayoutComponent>;
  }
  
  // Check permission-based access for protected routes
  if (!canAccessRoute(userPermissions, currentPath)) {
    return fallback;
  }

  // Check role requirements (for backward compatibility)
  if (requiredRoles.length > 0 && !hasAnyRole(requiredRoles)) {
    return fallback;
  }

  // Check single permission
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return fallback;
  }

  // Check multiple permissions
  if (requiredPermissions && requiredPermissions.length > 0) {
    const hasAccess = requireAll 
      ? hasAllPermissions(requiredPermissions)
      : hasAnyPermission(requiredPermissions);
    
    if (!hasAccess) {
      return fallback;
    }
  }

  // User has access, render the protected content with appropriate layout
  return <LayoutComponent>{children}</LayoutComponent>;
};

// PropTypes validation
ProtectedRoute.propTypes = {
  children: PropTypes.node.isRequired,
  requiredPermission: PropTypes.string, // Single permission to check
  requiredPermissions: PropTypes.arrayOf(PropTypes.string), // Array of permissions to check
  requireAll: PropTypes.bool, // If true, user must have ALL permissions
  requiredRoles: PropTypes.arrayOf(PropTypes.string), // For backward compatibility
  fallback: PropTypes.node,
  redirectTo: PropTypes.string,
};

export default ProtectedRoute;
export { PermissionDenied, LoadingSpinner };
